import type { NavigationGuardWithThis, Router } from 'vue-router'
import { useGlobalStore } from '@/stores/globalStore'
import { useCookie } from '@/lib/utils/useCookie'
import { XSRF_COOKIE_NAME } from '@/lib/utils/env'

/**
 * Global navigation guard — pure state reader.
 *
 * Auth state is owned by `globalStore` and hydrated once at app
 * boot via the store's `ready` Promise (see `globalStore.ts`).
 * This guard awaits that single Promise at the top and then makes
 * routing decisions purely from in-memory state. No server probe
 * happens here — ever — regardless of which branch fires.
 *
 * `await store.ready` is cheap after the first navigation: the
 * Promise has long settled, so subsequent awaits are microtask-only.
 *
 * Server-side session expiry is handled OUTSIDE this guard: any
 * authed API call returning 401 flips `globalStore.user` to null
 * via `useAPI.ts`'s catch handler; the store's watcher catches
 * that transition and routes to Login.
 */
export const useAuthGuard = (router: Router): NavigationGuardWithThis<undefined> => {
  return async (to, from) => {
    const globalStore = useGlobalStore()

    // Boot probe gates the first navigation; instant on every nav thereafter.
    await globalStore.ready

    // Cookie canary — the XSRF cookie's Max-Age mirrors session lifetime
    // (both refresh together on every Laravel response). If we hold
    // in-memory auth state but the cookie has expired / been swept,
    // the session is dead client-side. Clear state via the store's
    // single seam; the user-watcher handles the lockout side effects
    // (cancel in-flight, route to Login).
    //
    // Caveat: assumes browser clock is roughly in sync with the
    // server's. Modest skew is tolerated (cookie's Max-Age has slack);
    // pathological skew would just bounce to login on a still-valid
    // session, which is annoying but not broken.
    if (globalStore.user.detail && !useCookie().isCookieValid(XSRF_COOKIE_NAME)) {
      globalStore.clearAuth()
    }

    // Catch-all redirect for root.
    if (to.path === '/') {
      return globalStore.user.detail ? { name: 'Dashboard' } : { name: 'Login' }
    }

    // Validate the route exists.
    if (router.getRoutes().findIndex((r) => r.path === to.path || r.name === to.name) < 0) {
      return { name: 'ErrorPage', query: { error: 'NOT_FOUND' } }
    }

    // Guest routes — allow, EXCEPT externalAuth + logged-in → Dashboard.
    if (to.meta.guest) {
      if (to.meta.externalAuth && globalStore.user.detail) {
        return { name: 'Dashboard' }
      }
      return true
    }

    // Auth routes — gate on already-determined state. The
    // permission/role helpers internally short-circuit to true for
    // platform-tier actors (super_admin / system) — see
    // `buildUser.ts` for the canonical bypass.
    if (to.meta.auth) {
      if (!globalStore.user.detail) {
        return { name: 'Login' }
      }
      const requiredRoles = (to.meta.roles as string[] | undefined) ?? []
      const requiredPerms = (to.meta.permissions as string[] | undefined) ?? []
      if (requiredRoles.length && !globalStore.user.hasRoles(requiredRoles)) {
        return { name: 'Dashboard' }
      }
      if (requiredPerms.length && !globalStore.user.hasPermissions(requiredPerms)) {
        return { name: 'Dashboard' }
      }
      return true
    }

    return true
  }
}
